/**
 * HollyWarehouseCard — rich warehouse result card for the Holly chat.
 *
 * Layout:
 *   - 200px Mapbox preview with translucent radius circle
 *   - Anonymized title "Warehouse - N"
 *   - Pallet space line with clock icon (when capacity > 0)
 *   - Town/locality line (anonymized — never the full address or postcode)
 *   - "View Details" → sends a chat message ("View details for Warehouse - N")
 *     so Holly handles the request conversationally: pre-registration she asks
 *     for name + email (contact gate → registration CTA); post-contact she
 *     surfaces what the visitor has unlocked. The old inline
 *     HollyWarehouseDetailPanel only ever re-showed the card's anonymised
 *     fields — a dead end that never advanced the visitor to registration.
 *
 * The real warehouse.name is intentionally NOT rendered. Raw description text
 * is NOT rendered either — only extractTownFromDescription() output is shown.
 */

import { lazy, Suspense } from 'react';
import { useHolly } from './HollyContext';
import { extractTownFromDescription } from './anonymize';
import type { HollyWarehouse } from './types';

// mapbox-gl is ~1.7MB raw. A static import here would pull it into the chunk
// graph of every page that mounts the widget; React.lazy defers it until a
// warehouse card actually renders.
const HollyWarehouseMap = lazy(() => import('./HollyWarehouseMap'));

interface HollyWarehouseCardProps {
  warehouse: HollyWarehouse;
  warehouseIndex: number;
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={1.75}
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <circle cx='12' cy='13' r='8' />
      <path d='M12 9v4l2.5 2' />
      <path d='M9 2h6' />
      <path d='M12 2v3' />
    </svg>
  );
}

export default function HollyWarehouseCard({
  warehouse,
  warehouseIndex,
}: HollyWarehouseCardProps) {
  const { sendMessage } = useHolly();
  const displayLabel = `Warehouse - ${warehouseIndex}`;
  const town = extractTownFromDescription(warehouse.description);
  const capacity = warehouse.maxStorageCapacityAvailable;

  return (
    <div className='flex flex-col gap-2 w-full p-4 border border-gray-200 rounded-lg shadow-sm bg-white'>
      <div className='rounded-lg overflow-hidden max-h-[200px]'>
        <Suspense
          fallback={
            <div className='w-full h-[200px] bg-[#F3F4F6] animate-pulse' />
          }
        >
          <HollyWarehouseMap
            warehouse={warehouse}
            height='200px'
            zoom={10}
            circleRadiusPx={75}
          />
        </Suspense>
      </div>
      <div className='text-lg font-medium mb-2'>{displayLabel}</div>
      {typeof capacity === 'number' && capacity > 0 && (
        <div className='flex gap-2 items-center text-sm'>
          <ClockIcon className='w-5 h-5 text-gray-700' />
          <span>{capacity} pallet space available</span>
        </div>
      )}
      {town && <div className='text-sm text-gray-600'>{town}</div>}
      <div className='flex gap-4 mt-2'>
        <button
          type='button'
          className='flex-1 px-3 py-2 text-sm border border-black rounded-lg shadow-sm flex items-center justify-center bg-white hover:bg-gray-50'
          onClick={() => sendMessage(`View details for ${displayLabel}`)}
        >
          View Details
        </button>
      </div>
    </div>
  );
}
